import { CheckCircle, Edit } from "lucide-react";
import React from "react";
import { OPTION_LISTS } from "../../config/fieldConfig/optionLists";
import { useFieldResolver } from "../../hooks/useFieldResolver";
import { cn } from "../../lib/utils";
import type { KYCFormData } from "../../types";
import { openDocumentUrl, openOrDownloadUrl } from "../../utils/documentUrl";

interface KYCReviewProps {
	formData: KYCFormData;
	stepsWithErrors?: number[];
	onEdit: (step: number) => void;
}

/** Resolve an ISO alpha-2 code to a display name; falls back to the raw code. */
function resolveCountryLabel(code: string | undefined): string {
	if (!code) return "";
	return (
		OPTION_LISTS.COUNTRIES_ISO.find((o) => o.value === code)?.label.en ?? code
	);
}

// Fields that are rendered via custom JSX in each step — skip them in the
// config-driven loop so they aren't rendered twice.
const STEP1_CUSTOM_FIELDS = new Set([
	"registeredAddress",
	"proofOfBusinessUrl",
]);

const STEP3_CUSTOM_FIELDS = new Set([
	"ownershipTiers",
	"hasBoardOfDirectors",
	"boardOfDirectors",
	"consentForBackgroundChecks",
	"anyOwnerConvicted",
	"convictionDetails",
	"ownershipStructureChartUrl",
]);

const STEP6_CUSTOM_FIELDS = new Set([
	"primaryBusinessContact",
	"primaryProductContact",
	"primaryLegalContact",
	"primarySigner",
]);

const KYCReview: React.FC<KYCReviewProps> = ({
	formData,
	stepsWithErrors,
	onEdit,
}) => {
	const isApproved = formData.status === "approved";

	// Config-driven field resolution (pass formData for dependsOn evaluation)
	const { fields: resolvedFields } = useFieldResolver(
		{
			incorporationCountry: formData.countryOfIncorporation,
			entityType: formData.entityType,
			flowType: "KYB",
		},
		formData as unknown as Record<string, unknown>,
	);

	// Group resolved fields by step number
	const stepFields: Record<number, typeof resolvedFields> = {};
	for (const field of resolvedFields) {
		if (!stepFields[field.step]) stepFields[field.step] = [];
		stepFields[field.step]?.push(field);
	}

	const renderSection = (
		title: string,
		step: number,
		content: React.ReactNode,
	) => {
		const hasError = stepsWithErrors?.includes(step);
		return (
			<div
				data-kyc-section-step={step}
				className={cn(
					"mb-5 rounded-(--radius) border border-(--color-border) bg-(--color-background) p-6",
					hasError && "border border-(--color-error-500) bg-(--color-error-50)",
				)}
			>
				<div className="mb-4 flex items-center justify-between">
					<h3 className="m-0 text-lg">
						{title}
						{hasError && (
							<span className="ml-2 font-medium text-(--color-error-500) text-xs">
								— Needs attention
							</span>
						)}
					</h3>
					{/* Hide Edit button for approved applications */}
					{!isApproved && (
						<button
							type="button"
							className="inline-flex cursor-pointer items-center gap-1.5 rounded-(--radius) border border-(--color-border) bg-(--color-background) px-3 py-1.5 font-medium text-(--color-foreground) text-sm hover:bg-(--color-muted)"
							onClick={() => onEdit(step)}
						>
							<Edit size={14} />
							Edit
						</button>
					)}
				</div>
				{content}
			</div>
		);
	};

	const renderField = (label: string, value: unknown, fieldName?: string) => {
		if (!value || (Array.isArray(value) && value.length === 0)) return null;

		// Check if this is a URL field (attachment)
		const isUrl =
			typeof value === "string" &&
			(value.startsWith("http://") ||
				value.startsWith("https://") ||
				value.startsWith("blob:") ||
				value.startsWith("data:") ||
				label.toLowerCase().includes("url") ||
				label.toLowerCase().includes("document") ||
				label.toLowerCase().includes("attachment") ||
				label.toLowerCase().includes("upload") ||
				fieldName?.toLowerCase().includes("url"));

		return (
			<div className="mb-3">
				<strong className="mb-1 block text-(--color-muted-foreground) text-sm">
					{label}
				</strong>
				<div className="text-(--color-foreground)">
					{isUrl ? (
						<div className="flex items-center gap-2 rounded-[6px] border border-(--color-border) bg-(--color-muted) px-3 py-2">
							<span className="text-xl">📎</span>
							<button
								type="button"
								className="flex-1 cursor-pointer border-none bg-transparent p-0 text-left font-medium text-(--color-foreground) hover:underline focus:underline"
								onClick={() => openDocumentUrl(value)}
							>
								View Attachment
							</button>
							<button
								type="button"
								className="cursor-pointer rounded-[4px] border-none bg-(--color-primary-500) px-3 py-1 font-medium text-[13px] text-white hover:bg-(--color-primary-600)"
								onClick={() =>
									openOrDownloadUrl(value, `${label.replace(/\s+/g, "-")}.pdf`)
								}
							>
								Download
							</button>
						</div>
					) : typeof value === "boolean" ? (
						value ? (
							"Yes"
						) : (
							"No"
						)
					) : (
						String(value)
					)}
				</div>
			</div>
		);
	};

	/**
	 * Render a single FieldConfig entry from the resolved config.
	 * Resolves country display names for country-type fields.
	 * Returns null for complex nested fields (array, object) — those have
	 * their own custom rendering.
	 */
	const renderConfigField = (fieldConfig: (typeof resolvedFields)[number]) => {
		if (fieldConfig.type === "array") return null;

		const rawValue = (formData as unknown as Record<string, unknown>)[
			fieldConfig.fieldId
		];

		// Resolve country codes to display names
		let displayValue: unknown = rawValue;
		if (
			fieldConfig.options === "COUNTRIES_ISO" &&
			typeof rawValue === "string"
		) {
			displayValue = resolveCountryLabel(rawValue);
		} else if (
			fieldConfig.fieldId === "countryOfIncorporation" &&
			typeof rawValue === "string"
		) {
			displayValue = resolveCountryLabel(rawValue);
		}

		// Format currency fields
		if (
			fieldConfig.fieldId === "expectedMonthlyVolume" &&
			typeof rawValue === "number"
		) {
			displayValue = `$${rawValue.toLocaleString()}`;
		}

		return renderField(fieldConfig.label.en, displayValue, fieldConfig.fieldId);
	};

	return (
		<div className="w-full">
			{/* Status Banner - Changes based on approval state */}
			<div
				className={cn(
					"mb-6 flex items-center gap-3 rounded-(--radius) border p-5",
					formData.status === "approved"
						? "border-(--color-success-200) bg-(--color-success-50) dark:border-(--color-success-700) dark:bg-(--color-success-900)"
						: "border-(--color-primary-200) bg-(--color-primary-50) dark:border-(--color-primary-700) dark:bg-(--color-primary-900)",
				)}
			>
				<CheckCircle
					size={24}
					className={cn(
						formData.status === "approved"
							? "text-(--color-success-500)"
							: "text-(--color-primary-500)",
					)}
				/>
				<div>
					<h3 className="m-0 mb-1 text-lg">
						{formData.status === "approved"
							? "Application Approved"
							: "Application Complete"}
					</h3>
					<p className="m-0 text-(--color-muted-foreground) text-[15px]">
						{formData.status === "approved" && formData.approvedAt
							? `This application was approved by ${formData.approvedBy} on ${new Date(formData.approvedAt).toLocaleDateString()}`
							: "Review your information below before submitting. You can edit any section by clicking the Edit button."}
					</p>
				</div>
			</div>

			{/* Approval Record - Only shown when approved */}
			{formData.status === "approved" && formData.approvedBy && (
				<div className="mb-6 rounded-(--radius) border border-(--color-primary-500) bg-(--color-background) p-5">
					<h3 className="m-0 mb-3 text-(--color-primary-500) text-base">
						📋 Approval Record
					</h3>
					<div className="grid gap-2">
						<div className="flex justify-between">
							<strong className="text-(--color-muted-foreground)">
								Approved By:
							</strong>
							<span className="text-(--color-foreground)">
								{formData.approvedBy}
							</span>
						</div>
						{formData.approvedAt && (
							<div className="flex justify-between">
								<strong className="text-(--color-muted-foreground)">
									Approval Date:
								</strong>
								<span className="text-(--color-foreground)">
									{new Date(formData.approvedAt).toLocaleString()}
								</span>
							</div>
						)}
						<div className="flex justify-between">
							<strong className="text-(--color-muted-foreground)">
								Application ID:
							</strong>
							<span className="font-mono text-(--color-foreground) text-sm">
								{formData.id}
							</span>
						</div>
					</div>
				</div>
			)}

			{/* Step 1: Company Basics — config-driven left column + custom address/proof right column */}
			{renderSection(
				"Company Basics",
				1,
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div>
						{(stepFields[1] ?? [])
							.filter((f) => !STEP1_CUSTOM_FIELDS.has(f.fieldId))
							.map((f) => (
								<React.Fragment key={f.fieldId}>
									{renderConfigField(f)}
								</React.Fragment>
							))}
					</div>
					<div>
						<strong className="mb-2 block text-(--color-muted-foreground) text-sm">
							Registered Address
						</strong>
						<div className="mb-3 text-(--color-foreground) text-[15px]">
							{formData.registeredAddress.address1}
							<br />
							{formData.registeredAddress.address2 && (
								<>
									{formData.registeredAddress.address2}
									<br />
								</>
							)}
							{formData.registeredAddress.city},{" "}
							{formData.registeredAddress.stateProvince}{" "}
							{formData.registeredAddress.zip}
							<br />
							{resolveCountryLabel(formData.registeredAddress.country)}
						</div>
						{renderField("Business Description", formData.businessDescription)}
						{renderField(
							"Proof of Business",
							formData.proofOfBusinessUrl,
							"proofOfBusinessUrl",
						)}
					</div>
				</div>,
			)}

			{/* Step 2: Wallet & Technical — fully config-driven */}
			{renderSection(
				"Wallet & Technical Details",
				2,
				(stepFields[2] ?? []).map((f) => (
					<React.Fragment key={f.fieldId}>
						{renderConfigField(f)}
					</React.Fragment>
				)),
			)}

			{/* Step 3: Ownership (conditional) — show when has data OR has errors for highlighting */}
			{(formData.numberOfOwnershipTiers > 1 ||
				formData.ownershipTiers.length > 0 ||
				stepsWithErrors?.includes(3)) &&
				renderSection(
					"Ownership & UBO",
					3,
					<>
						{renderField(
							"Number of Ownership Tiers",
							formData.numberOfOwnershipTiers,
						)}
						{formData.ownershipTiers.length > 0 && (
							<div className="mt-4">
								{formData.ownershipTiers.map((tier, idx) => (
									<div
										key={`tier-${idx}-${tier.ownershipPercentage || 0}-${tier.ownerType || "unknown"}`}
										className="mb-3 rounded-(--radius) border border-(--color-border) bg-(--color-muted) p-3"
									>
										<strong>Tier {tier.tierNumber}</strong> - {tier.ownerType} (
										{tier.ownershipPercentage}%)
										{tier.fullName && (
											<div className="mt-2 text-sm">Owner: {tier.fullName}</div>
										)}
										{tier.corporateOwnersList && (
											<div className="mt-1 text-green-600 text-sm">
												✓ Corporate owners list uploaded
											</div>
										)}
										{tier.photoIdUrl && (
											<div className="mt-1 text-green-600 text-sm">
												✓ Photo ID uploaded
											</div>
										)}
									</div>
								))}
							</div>
						)}
						{renderField(
							"Consent for Background Checks",
							formData.consentForBackgroundChecks,
						)}
						{renderField("Any Owner Convicted", formData.anyOwnerConvicted)}
						{formData.convictionDetails &&
							renderField("Conviction Details", formData.convictionDetails)}

						{formData.hasBoardOfDirectors !== undefined &&
							renderField(
								"Has Board of Directors",
								formData.hasBoardOfDirectors,
							)}

						{formData.hasBoardOfDirectors &&
							formData.boardOfDirectors &&
							formData.boardOfDirectors.length > 0 && (
								<div className="mt-4">
									<strong className="mb-2 block text-sm">
										Board of Directors
									</strong>
									{formData.boardOfDirectors.map((director, idx) => (
										<div
											key={`director-${director.fullName ?? String(director.ownerIndex ?? idx)}`}
											className="mb-3 rounded-(--radius) border border-(--color-border) bg-(--color-muted) p-3"
										>
											{director.isExistingOwner &&
											director.ownerIndex !== undefined ? (
												<div>
													<strong>Director {idx + 1}</strong> - From Owner{" "}
													{
														formData.ownershipTiers[director.ownerIndex]
															?.tierNumber
													}
													<div className="mt-1 text-green-600 text-sm">
														✓ Information pulled from owner details
													</div>
												</div>
											) : (
												<div>
													<strong>Director {idx + 1}</strong>
													{director.fullName && (
														<div className="mt-2 text-sm">
															Name: {director.fullName}
														</div>
													)}
													{director.dateOfBirth && (
														<div className="mt-1 text-sm">
															Date of Birth: {director.dateOfBirth}
														</div>
													)}
													{director.nationality && (
														<div className="mt-1 text-sm">
															Nationality: {director.nationality}
														</div>
													)}
													{director.address && (
														<div className="mt-1 text-sm">
															Address: {director.address}
														</div>
													)}
												</div>
											)}
										</div>
									))}
								</div>
							)}

						{/* Remaining config fields for step 3 not already rendered above */}
						{(stepFields[3] ?? [])
							.filter((f) => !STEP3_CUSTOM_FIELDS.has(f.fieldId))
							.map((f) => (
								<React.Fragment key={f.fieldId}>
									{renderConfigField(f)}
								</React.Fragment>
							))}
					</>,
				)}

			{/* Step 4: Compliance — fully config-driven except the licenses array */}
			{renderSection(
				"Compliance & Risk",
				4,
				(stepFields[4] ?? []).map((f) => {
					// The licenses array gets custom rendering
					if (f.fieldId === "licenses") {
						return formData.licenses.length > 0 ? (
							<div key={f.fieldId} className="mb-4">
								<strong className="mb-2 block text-(--color-muted-foreground) text-sm">
									Licenses ({formData.licenses.length})
								</strong>
								{formData.licenses.map((license, idx) => (
									<div
										key={`license-${idx}-${license.licenseName || ""}-${license.jurisdiction || ""}`}
										className="mb-1 text-(--color-foreground) text-sm"
									>
										• {license.licenseName} - {license.jurisdiction}
									</div>
								))}
							</div>
						) : null;
					}
					// The complianceOfficer object — render name + email sub-fields
					if (f.fieldId === "complianceOfficer") {
						return (
							<React.Fragment key={f.fieldId}>
								{renderField(
									"Compliance Officer",
									formData.complianceOfficer.name,
								)}
								{renderField(
									"Compliance Officer Email",
									formData.complianceOfficer.email,
								)}
							</React.Fragment>
						);
					}
					return (
						<React.Fragment key={f.fieldId}>
							{renderConfigField(f)}
						</React.Fragment>
					);
				}),
			)}

			{/* Step 5: Limits — fully config-driven */}
			{renderSection(
				"Limits & Restrictions",
				5,
				(stepFields[5] ?? []).map((f) => (
					<React.Fragment key={f.fieldId}>
						{renderConfigField(f)}
					</React.Fragment>
				)),
			)}

			{/* Step 6: Contacts — custom 2-column layout + remaining config fields */}
			{renderSection(
				"Contacts & Final Information",
				6,
				<>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<div>
							<h5 className="mb-3 font-semibold text-[15px]">
								Primary Business Contact
							</h5>
							{renderField("Name", formData.primaryBusinessContact.name)}
							{renderField("Email", formData.primaryBusinessContact.email)}
							{renderField("Phone", formData.primaryBusinessContact.phone)}

							<h5 className="mt-5 mb-3 font-semibold text-[15px]">
								Primary Product Contact
							</h5>
							{renderField("Name", formData.primaryProductContact.name)}
							{renderField("Email", formData.primaryProductContact.email)}
							{renderField("Phone", formData.primaryProductContact.phone)}
						</div>
						<div>
							<h5 className="mb-3 font-semibold text-[15px]">
								Primary Legal Contact
							</h5>
							{renderField("Name", formData.primaryLegalContact.name)}
							{renderField("Email", formData.primaryLegalContact.email)}
							{renderField("Phone", formData.primaryLegalContact.phone)}

							<h5 className="mt-5 mb-3 font-semibold text-[15px]">
								Primary Signer
							</h5>
							{renderField("Name", formData.primarySigner.name)}
							{renderField("Role", formData.primarySigner.role)}
							{renderField("Email", formData.primarySigner.email)}
							{renderField("Phone", formData.primarySigner.phone)}
						</div>
					</div>

					{/* Remaining config fields for step 6 not covered by contacts */}
					{(stepFields[6] ?? [])
						.filter((f) => !STEP6_CUSTOM_FIELDS.has(f.fieldId))
						.map((f) => (
							<React.Fragment key={f.fieldId}>
								{renderConfigField(f)}
							</React.Fragment>
						))}
				</>,
			)}

			{formData.ownershipStructureChartUrl && (
				<div className="mt-5 flex items-center gap-2 rounded-(--radius) border border-(--color-primary-200) bg-(--color-primary-50) dark:border-(--color-primary-700) dark:bg-(--color-primary-900) p-4">
					<CheckCircle size={18} className="shrink-0 text-green-600" />
					<strong>Ownership Structure Chart uploaded</strong>
				</div>
			)}

			{renderField(
				"Ownership Structure Chart",
				formData.ownershipStructureChartUrl,
				"ownershipStructureChartUrl",
			)}
		</div>
	);
};

export default KYCReview;
